import React, { useEffect, useMemo, useRef } from "react";
import {
  KeyValueCustomSelect,
  NumberInputField,
  Text,
  useSelectState,
} from "@smartsuite/react-ui/lib";

import {
  DocumentDesignItem,
  DocumentDesignItemType,
  DocumentDesignState,
} from "../../canvas/state/types";
import { RenderContext } from "../../canvas/renderer/fabric-renderer";

interface SidebarSelectionConfigRelativeOptionsProps {
  state: DocumentDesignState;
  context: RenderContext;
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

export const SidebarSelectionConfigRelativeOptions: React.FC<
  SidebarSelectionConfigRelativeOptionsProps
> = ({ state, context, selected, onChangeSelected }) => {
  const anchorDistanceRef = useRef<HTMLInputElement>(null);
  const [anchorDistanceSelectState, setAnchorDistanceSelectState] =
    useSelectState();
  const [anchorSelectState, setAnchorSelectState] = useSelectState();

  const availableOptions = useMemo(() => {
    return state.items
      .filter((item) => {
        const isSameItem = item.id === selected.id;
        if (isSameItem) {
          return false;
        }

        const isAbsolutePosition = item.position !== "anchored"; // Absolute positions are either falsy or "absolute"
        if (isAbsolutePosition) {
          return true;
        }

        // Iterate over state items to ensure there's no circular reference
        let isRelativeAnchor = false;
        let anchor = item;

        while (anchor.anchorId) {
          const relativeAnchor = state.items.find(
            (item) => item.id === anchor.anchorId,
          );
          if (!relativeAnchor) {
            break;
          }

          if (relativeAnchor.id === selected.id) {
            isRelativeAnchor = true;
            break;
          }

          anchor = relativeAnchor;
        }

        return !isRelativeAnchor;
      })
      .sort((a, b) => a.top - b.top)
      .map<SelectOption>((item) => ({
        value: item.id,
        label: composeItemLabel(item, context),
      }));
  }, [state, selected, context]);

  // Set default anchor if none is selected
  useEffect(() => {
    if (!selected.anchorId && availableOptions.length) {
      onChangeSelected({
        ...selected,
        anchorId: availableOptions[0].value,
      });
    }
  }, [selected, availableOptions, onChangeSelected]);

  return (
    <>
      <div>
        <div style={{ marginBottom: 4 }}>
          <Text
            weight="medium"
            color={anchorSelectState ? "link" : "primary"}
            wrapMode="ellipsis"
          >
            Anchor
          </Text>
        </div>
        <KeyValueCustomSelect<"value", "label", SelectOption>
          state={anchorSelectState}
          options={availableOptions}
          showClear={false}
          placeholderArrowStyle="filled-dark"
          value={selected.anchorId}
          onChange={(value) =>
            onChangeSelected({
              ...selected,
              anchorId: value,
            })
          }
          onOpenedChange={setAnchorSelectState}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4 }}>
          <Text
            weight="medium"
            color={anchorDistanceSelectState ? "link" : "primary"}
            wrapMode="ellipsis"
          >
            Distance from anchor
          </Text>
        </div>
        <NumberInputField
          ref={anchorDistanceRef}
          onFocus={() => setAnchorDistanceSelectState(true)}
          onBlur={() => setAnchorDistanceSelectState(false)}
          state={anchorDistanceSelectState ? "active" : undefined}
          showSymbol={false}
          autoHideNavigation
          inputStyles={{
            backgroundColor: "white",
            borderColor: "G-1-5",
            borderHoverColor: "G-3",
            borderFocusColor: "G-3",
          }}
          max="999"
          min="0"
          value={`${selected.anchorDistance ?? 0}`}
          onChangeValue={(value) => {
            if (anchorDistanceRef.current) anchorDistanceRef.current.focus();

            const parsedValue = parseInt(value, 10);
            onChangeSelected({
              ...selected,
              anchorDistance: isNaN(parsedValue) ? 0 : parsedValue,
            });
          }}
        />
      </div>
    </>
  );
};

function composeItemLabel(
  item: DocumentDesignItem,
  context: RenderContext,
): string {
  const fieldName = getFieldName(item, context);
  const coordinates = `(${item.left}, ${item.top})`;

  return `${fieldName} ${coordinates}`;
}

function getFieldName(
  item: DocumentDesignItem,
  context: RenderContext,
): string {
  if (item.type === DocumentDesignItemType.rect) {
    return "Rectangle";
  }

  if ("field_slug" in item) {
    const field = context.application?.structure?.find(
      (field) => field.slug === item.field_slug,
    );
    if (field) {
      return field.label;
    }
  }

  return item.type.charAt(0).toUpperCase() + item.type.slice(1);
}
